// client/src/pages/investments/InvestmentsOverview.js

import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Card, Col, Row, Spinner, Table } from 'react-bootstrap';
import Chart from 'react-apexcharts';
import { FaChartLine, FaPiggyBank, FaBitcoin, FaArrowRight } from 'react-icons/fa';
import usePea from '../../hooks/usePea';
import useCrypto from '../../hooks/useCrypto';
import { CHART_PALETTE, CHART_GRID_COLOR } from '../../utils/chartTheme';

const eur = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const signedEur = (v) => `${Number(v) >= 0 ? '+' : ''}${eur(v)}`;
const pct = (v) => `${Number(v) >= 0 ? '+' : ''}${Number(v || 0).toFixed(2)} %`;

const MetricCard = ({ title, value, subtitle, tone = 'neutral', icon: Icon }) => {
  const color = tone === 'pos' ? 'text-success' : tone === 'neg' ? 'text-danger' : '';
  return (
    <Col lg={3} sm={6}>
      <Card className="border-0 shadow-sm h-100">
        <Card.Body className="p-4">
          <div className="small fw-bold text-muted text-uppercase mb-2 d-flex align-items-center">
            {Icon && <Icon className="me-2" size={13} />}
            {title}
          </div>
          <div className={`h3 fw-bold mb-0 ${color}`}>{value}</div>
          {subtitle && <div className="small text-muted mt-2 opacity-75">{subtitle}</div>}
        </Card.Body>
      </Card>
    </Col>
  );
};

/**
 * Fusionne les historiques PEA et Crypto sur un axe de dates commun.
 * Chaque poche conserve sa dernière valeur connue aux dates où elle n'a pas de
 * point : sans ce report, une poche relevée moins souvent que l'autre ferait
 * plonger la courbe totale un jour sur deux.
 */
const mergeHistories = (peaHistory = [], cryptoHistory = []) => {
  const dates = [
    ...new Set([...peaHistory.map((h) => h.date), ...cryptoHistory.map((h) => h.date)])
  ].sort();
  if (dates.length === 0) return [];

  const peaByDate = new Map(peaHistory.map((h) => [h.date, Number(h.totalValue)]));
  const cryptoByDate = new Map(cryptoHistory.map((h) => [h.date, Number(h.totalValue)]));

  let lastPea = 0;
  let lastCrypto = 0;
  return dates.map((date) => {
    if (peaByDate.has(date)) lastPea = peaByDate.get(date);
    if (cryptoByDate.has(date)) lastCrypto = cryptoByDate.get(date);
    return { date, pea: lastPea, crypto: lastCrypto, total: lastPea + lastCrypto };
  });
};

const ValueChart = ({ history }) => {
  const chart = useMemo(() => {
    if (!history || history.length < 2) return null;
    return {
      categories: history.map((h) =>
        new Date(h.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
      ),
      pea: history.map((h) => Number(h.pea.toFixed(2))),
      crypto: history.map((h) => Number(h.crypto.toFixed(2)))
    };
  }, [history]);

  if (!chart) {
    return (
      <p className="text-muted mb-0">
        La courbe apparaîtra après au moins deux relevés à des dates différentes.
      </p>
    );
  }

  return (
    <Chart
      type="area"
      height={280}
      series={[
        { name: 'PEA', data: chart.pea },
        { name: 'Crypto', data: chart.crypto }
      ]}
      options={{
        chart: { toolbar: { show: false }, stacked: true },
        colors: [CHART_PALETTE[0], CHART_PALETTE[1]],
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 2 },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0.05 } },
        xaxis: {
          categories: chart.categories,
          labels: { rotate: -45, style: { fontSize: '11px' } }
        },
        yaxis: { labels: { formatter: (v) => eur(v) } },
        tooltip: { y: { formatter: (v) => eur(v) } },
        legend: { position: 'top', horizontalAlign: 'right' },
        grid: { borderColor: CHART_GRID_COLOR }
      }}
    />
  );
};

const AllocationChart = ({ allocation }) => {
  if (!allocation || allocation.length === 0) return null;
  return (
    <Chart
      type="donut"
      height={280}
      series={allocation.map((a) => Number(a.amount.toFixed(2)))}
      options={{
        labels: allocation.map((a) => a.label),
        colors: CHART_PALETTE,
        legend: { position: 'bottom' },
        dataLabels: { formatter: (v) => `${Number(v).toFixed(1)} %` },
        tooltip: { y: { formatter: (v) => eur(v) } },
        plotOptions: { pie: { donut: { size: '62%' } } }
      }}
    />
  );
};

function InvestmentsOverview() {
  const { overview: pea, loading: peaLoading, error: peaError, fetchOverview: fetchPea } = usePea();
  const {
    overview: crypto,
    loading: cryptoLoading,
    error: cryptoError,
    fetchOverview: fetchCrypto
  } = useCrypto();

  useEffect(() => {
    fetchPea().catch(() => {
      // erreur déjà remontée par le hook
    });
    fetchCrypto().catch(() => {
      // erreur déjà remontée par le hook
    });
  }, [fetchPea, fetchCrypto]);

  const peaValue = pea ? pea.totals.totalWithCash : 0;
  const cryptoValue = crypto ? crypto.totals.totalValue : 0;
  const totalValue = peaValue + cryptoValue;

  // Le PEA fournit toujours un PRU ; la crypto seulement si l'utilisateur l'a
  // saisi. On n'additionne donc que ce qui est réellement comparable.
  const totalInvested =
    (pea ? pea.totals.totalInvested : 0) + (crypto ? crypto.totals.totalInvested : 0);
  const gain =
    (pea ? pea.totals.gain : 0) + (crypto && crypto.totals.gain ? crypto.totals.gain : 0);
  const gainPct = totalInvested > 0 ? (gain / totalInvested) * 100 : 0;

  const history = useMemo(
    () => mergeHistories(pea ? pea.history : [], crypto ? crypto.history : []),
    [pea, crypto]
  );

  // Allocation combinée : lignes du PEA + tokens crypto, poche identifiée.
  const allocation = useMemo(() => {
    const rows = [];
    if (pea) {
      pea.allocation.forEach((a) =>
        rows.push({ key: `pea-${a.isin}`, label: a.name, pocket: 'PEA', amount: a.amount })
      );
    }
    if (crypto) {
      crypto.allocation.forEach((a) =>
        rows.push({
          key: `crypto-${a.symbol}`,
          label: a.symbol,
          pocket: 'Crypto',
          amount: a.amount
        })
      );
    }
    return rows
      .map((r) => ({ ...r, share: totalValue > 0 ? (r.amount / totalValue) * 100 : 0 }))
      .sort((a, b) => b.amount - a.amount);
  }, [pea, crypto, totalValue]);

  if ((peaLoading.overview && !pea) || (cryptoLoading.overview && !crypto)) {
    return <Spinner animation="border" />;
  }

  const hasAnything = allocation.length > 0;
  const error = peaError || cryptoError;

  return (
    <div>
      <h2 className="mb-4">Vue d&apos;ensemble</h2>

      {error && <Alert variant="danger">{error}</Alert>}

      {!hasAnything ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-5 text-center">
            <FaPiggyBank size={36} className="text-muted mb-3" />
            <h5>Aucun investissement pour le moment</h5>
            <p className="text-muted mb-4">
              Importez l&apos;export de portefeuille de votre PEA, ou suivez un wallet crypto.
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Link to="/investments/pea" className="btn btn-primary">
                Gérer mon PEA <FaArrowRight size={12} className="ms-1" />
              </Link>
              <Link to="/investments/crypto" className="btn btn-outline-primary">
                Suivre un wallet <FaArrowRight size={12} className="ms-1" />
              </Link>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <MetricCard
              title="Total investissements"
              value={eur(totalValue)}
              subtitle={`${allocation.length} ligne(s)`}
              icon={FaChartLine}
            />
            <MetricCard
              title="PEA"
              value={eur(peaValue)}
              subtitle={pea ? `dont ${eur(pea.totals.totalCash)} de liquidités` : undefined}
              icon={FaPiggyBank}
            />
            <MetricCard
              title="Crypto"
              value={eur(cryptoValue)}
              subtitle={
                crypto && crypto.wallets.length > 0
                  ? `${crypto.wallets.length} wallet(s)`
                  : 'aucun wallet'
              }
              icon={FaBitcoin}
            />
            <MetricCard
              title="Plus-value latente"
              value={signedEur(gain)}
              subtitle={totalInvested > 0 ? pct(gainPct) : 'prix de revient non renseigné'}
              tone={gain >= 0 ? 'pos' : 'neg'}
            />
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={8}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <h6 className="fw-bold mb-3">Évolution</h6>
                  <ValueChart history={history} />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <h6 className="fw-bold mb-3">Allocation</h6>
                  <AllocationChart allocation={allocation} />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
              <h6 className="fw-bold mb-3">Répartition par ligne</h6>
              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr className="text-muted" style={{ fontSize: 12 }}>
                      <th>Support</th>
                      <th>Poche</th>
                      <th className="text-end">Valorisation</th>
                      <th className="text-end">Poids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocation.map((a) => (
                      <tr key={a.key}>
                        <td className="fw-semibold">{a.label}</td>
                        <td>
                          <Badge bg={a.pocket === 'PEA' ? 'primary' : 'warning'} text="dark">
                            {a.pocket}
                          </Badge>
                        </td>
                        <td className="text-end">{eur(a.amount)}</td>
                        <td className="text-end">
                          <Badge bg="light" text="dark">
                            {a.share.toFixed(1)} %
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
}

export default InvestmentsOverview;
